/**
 * ColdStartTracker — Tracks cold start performance marks and sends metrics to Datadog RUM.
 *
 * ## Usage
 * 1. Call `ColdStartTracker.markModuleLoad()` at module load time in `app/_layout.tsx`.
 * 2. Call `ColdStartTracker.mark(phase)` at each startup phase.
 * 3. Call `ColdStartTracker.complete()` when the splash screen is hidden (cold start is over).
 *
 * All timings are relative to `ModuleLoadTime` (T0) and are sent as a single Datadog RUM action
 * with per-phase durations plus the total cold start duration, enabling P70/P90/P95 analysis
 * in Datadog dashboards.
 */
import { DdRum, RumActionType } from "@/crossPlatform/Datadog";
import { logInfo } from "@/services/AppMonitor";

/**
 * Phases of the cold start lifecycle, in expected chronological order.
 * Each phase is marked with a `performance.now()` timestamp relative to T0 (module load).
 */
// IMPORTANT: Enum string values are sent to Datadog RUM and are part of the API contract.
enum ColdStartPhase {
  /** Datadog SDK initialization complete */
  DatadogInitialized = "datadog_initialized",
  /** Custom fonts have finished loading */
  FontsLoaded = "fonts_loaded",
  /** JS entry point (index.js) begins executing — earliest possible mark */
  JsEntryStart = "js_entry_start",
  /** LaunchDarkly feature flags ready */
  LaunchDarklyReady = "launch_darkly_ready",
  /** Module-level code in _layout.tsx begins executing */
  ModuleLoad = "module_load",
  /** Root navigator determined auth state and is ready */
  NavigatorReady = "navigator_ready",
  /** Splash screen hidden — cold start is complete */
  SplashHidden = "splash_hidden",
}

/** Datadog RUM action type used for custom timing events. */
const CustomRumActionType = RumActionType.CUSTOM;

/** Name of the Datadog RUM action that carries cold start metrics. */
const ColdStartActionName = "cold_start";

type PhaseTimestamps = Partial<Record<ColdStartPhase, number>>;

interface ColdStartMetrics {
  /** Total cold start duration in ms (module load → splash hidden) */
  cold_start_duration_ms: number;
  /** Per-phase durations between consecutive phases (ms) */
  phase_durations: Record<string, number>;
  /** Per-phase elapsed times from module load (ms) */
  phase_timestamps: Record<string, number>;
}

/**
 * Ordered list of phases for calculating inter-phase durations.
 * Must match the expected chronological order of startup.
 */
const PhaseOrder: ColdStartPhase[] = [
  ColdStartPhase.JsEntryStart,
  ColdStartPhase.ModuleLoad,
  ColdStartPhase.FontsLoaded,
  ColdStartPhase.DatadogInitialized,
  ColdStartPhase.LaunchDarklyReady,
  ColdStartPhase.NavigatorReady,
  ColdStartPhase.SplashHidden,
];

/** Singleton tracker — cold start only happens once per app launch. */
let Marks: PhaseTimestamps = {};
let ModuleLoadTime = 0;
let isCompleted = false;

/**
 * Records the JS entry start timestamp. Called from `index.js` — the true app entry point.
 * This becomes T0 (always 0); all subsequent phases are measured relative to this.
 * @param timestamp - The value of `performance.now()` captured at the top of index.js.
 */
const markJsEntryStart = (timestamp: number): void => {
  ModuleLoadTime = timestamp;
  Marks[ColdStartPhase.JsEntryStart] = 0;
};

/**
 * Records the module load timestamp (when `app/_layout.tsx` begins executing).
 * Recorded as a positive offset from T0 (index.js entry).
 * If `markJsEntryStart` was not called, this falls back to being T0.
 * @param timestamp - The value of `performance.now()` captured at module load.
 */
const markModuleLoad = (timestamp: number): void => {
  if (ModuleLoadTime === 0) {
    ModuleLoadTime = timestamp;
    Marks[ColdStartPhase.ModuleLoad] = 0;
    return;
  }
  Marks[ColdStartPhase.ModuleLoad] = timestamp - ModuleLoadTime;
};

/**
 * Records a performance mark for a specific startup phase.
 * @param phase - The startup phase being marked.
 */
const mark = (phase: ColdStartPhase): void => {
  if (isCompleted) return;
  // Use markModuleLoad() instead
  if (phase === ColdStartPhase.ModuleLoad) return;

  const elapsed = performance.now() - ModuleLoadTime;
  Marks[phase] = elapsed;

  logInfo({
    context: { elapsed_ms: elapsed, phase },
    message: `[ColdStart] Phase marked: ${phase} at ${elapsed.toFixed(1)}ms`,
  });
};

/**
 * Computes inter-phase durations from the recorded timestamps.
 * Only includes phases that have been marked.
 */
const computePhaseDurations = (): Record<string, number> => {
  const durations: Record<string, number> = {};
  const recordedPhases = PhaseOrder.filter((phase) => Marks[phase] != null);

  for (let i = 1; i < recordedPhases.length; i++) {
    const prevPhase = recordedPhases[i - 1];
    const currPhase = recordedPhases[i];
    const prevTime = Marks[prevPhase] ?? 0;
    const currTime = Marks[currPhase] ?? 0;

    durations[`${prevPhase}_to_${currPhase}`] = Math.round(currTime - prevTime);
  }

  return durations;
};

/**
 * Builds the final metrics payload from all recorded marks.
 */
const buildMetrics = (): ColdStartMetrics | null => {
  const splashTime = Marks[ColdStartPhase.SplashHidden];
  if (splashTime == null) return null;

  const phaseTimestamps: Record<string, number> = {};
  for (const [phase, time] of Object.entries(Marks)) {
    phaseTimestamps[phase] = Math.round(time);
  }

  return {
    cold_start_duration_ms: Math.round(splashTime),
    phase_durations: computePhaseDurations(),
    phase_timestamps: phaseTimestamps,
  };
};

/**
 * Marks the cold start as complete and sends all metrics to Datadog RUM.
 * This should be called once the splash screen has been hidden.
 *
 * Sends a custom RUM action `cold_start` with attributes:
 * - `cold_start.duration_ms` — Total cold start time
 * - `cold_start.phase.<phase>_ms` — Elapsed time from T0 to each phase
 * - `cold_start.between.<from>_to_<to>_ms` — Duration between consecutive phases
 */
const complete = (): void => {
  if (isCompleted) return;

  mark(ColdStartPhase.SplashHidden);
  isCompleted = true;

  const metrics = buildMetrics();
  if (!metrics) return;

  logInfo({
    context: {
      duration_ms: metrics.cold_start_duration_ms,
      phases: metrics.phase_timestamps,
    },
    message: `[ColdStart] Complete: ${metrics.cold_start_duration_ms}ms`,
  });

  // Build flat attributes for Datadog RUM faceting/aggregation
  const attributes: Record<string, number> = {
    "cold_start.duration_ms": metrics.cold_start_duration_ms,
  };

  for (const [phase, time] of Object.entries(metrics.phase_timestamps)) {
    attributes[`cold_start.phase.${phase}_ms`] = time;
  }

  for (const [key, duration] of Object.entries(metrics.phase_durations)) {
    attributes[`cold_start.between.${key}_ms`] = duration;
  }

  // Send as a custom RUM action — this appears in Datadog RUM Explorer
  // and can be used in dashboards for P70/P90/P95 percentile analysis
  void DdRum.addAction(
    CustomRumActionType,
    ColdStartActionName,
    attributes,
    Date.now(),
  );
};

/**
 * Resets all state. Only used for testing — cold start happens once per app launch in production.
 */
const reset = (): void => {
  Marks = {};
  ModuleLoadTime = 0;
  isCompleted = false;
};

/** Read-only access to current state for testing. */
const getState = (): {
  isCompleted: boolean;
  marks: PhaseTimestamps;
  moduleLoadTime: number;
} => ({
  isCompleted,
  marks: { ...Marks },
  moduleLoadTime: ModuleLoadTime,
});

const ColdStartTracker = {
  buildMetrics,
  complete,
  getState,
  mark,
  markJsEntryStart,
  markModuleLoad,
  reset,
};

export { ColdStartPhase };
export default ColdStartTracker;
